import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ContextMenu.css';

// A right-click menu. `menu` is null (closed) or { x, y, items, anchor?, align? }, with x/y in viewport
// pixels. Items are { key, label, icon?, danger?, disabled?, onSelect } or { key, divider: true }. The
// menu opens at the point (or, with align 'end', ending at it, for a menu button's right edge), kept
// inside the viewport, and closes on a pick, Escape, a click elsewhere, scroll or resize. A click on
// `anchor` (the button that opened it) is left to that button, so it can toggle the menu shut. Arrow
// keys, Home/End and Enter work like a native menu, and focus returns to where it was on close.
export default function ContextMenu({ menu, onClose, label = 'Actions' }) {
  const ref = useRef(null);
  const returnFocusRef = useRef(null);
  const [pos, setPos] = useState(null);

  const close = useCallback(() => {
    onClose?.();
    returnFocusRef.current?.focus?.({ preventScroll: true });
  }, [onClose]);

  // Place it at the point, flipping left/up when it would run off the right or bottom edge.
  useLayoutEffect(() => {
    if (!menu) {
      setPos(null);
      return;
    }
    returnFocusRef.current = menu.anchor ?? document.activeElement;
    const el = ref.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    const margin = 8;
    let left = menu.align === 'end' ? menu.x - w : menu.x;
    let top = menu.y;
    if (left + w > window.innerWidth - margin) left = menu.x - w;
    left = Math.max(margin, left);
    if (top + h > window.innerHeight - margin) top = Math.max(margin, menu.y - h);
    setPos({ left, top });
  }, [menu]);

  useEffect(() => {
    if (!pos) return;
    ref.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
  }, [pos]);

  useEffect(() => {
    if (!menu) return undefined;
    const onPointerDown = (e) => {
      if (ref.current?.contains(e.target) || menu.anchor?.contains(e.target)) return;
      onClose?.();
    };
    const dismiss = () => onClose?.();
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  const onKeyDown = (e) => {
    const items = Array.from(ref.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') ?? []);
    const i = items.indexOf(document.activeElement);
    const focusAt = (n) => items[(n + items.length) % items.length]?.focus();
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt(i + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAt(items.length - 1);
    }
  };

  return createPortal(
    <div
      ref={ref}
      className="ppc-context-menu"
      role="menu"
      aria-label={label}
      style={pos ? { left: pos.left, top: pos.top } : { left: menu.x, top: menu.y, visibility: 'hidden' }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {menu.items.map((item) =>
        item.divider ? (
          <div key={item.key} className="ppc-context-menu__divider" role="separator" />
        ) : (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={`ppc-context-menu__item${item.danger ? ' ppc-context-menu__item--danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              onClose?.();
              item.onSelect?.();
            }}
          >
            {item.icon ? <i className={`bi ${item.icon}`} aria-hidden="true" /> : null}
            <span>{item.label}</span>
          </button>
        )
      )}
    </div>,
    document.body
  );
}

// Viewport point to open a context menu for a keyboard request (the Menu key or Shift+F10), placed
// just inside the focused element.
export const contextMenuPointFor = (element) => {
  const r = element.getBoundingClientRect();
  return { x: r.left + 16, y: r.top + Math.min(r.height, 32) };
};
